import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Icon } from "@iconify/react";
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";

import NotificationBell from "js/components/NotificationBell";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "js/components/ui/avatar";
import { Badge } from "js/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "js/components/ui/breadcrumb";
import { Button } from "js/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "js/components/ui/dropdown-menu";

import { getBreadcrumbsForRoute } from "js/config/breadcrumbConfig";

import { logout } from "../../store/features/auth/auth";
import { membershipDataCleared } from "../../store/features/memberhsip/membership";
import { organizationDataCleared } from "../../store/features/organization/organization";
import { userDataCleared } from "../../store/features/user/user";
import { useSessionData } from "../../hooks/useSessionData";

const Header = ({
  setNavCollapsed,
  isNavCollapsed,
}: {
  setNavCollapsed: (collapsed: boolean) => void;
  isNavCollapsed: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useSelector((state: any) => state.entities.user);
  const dispatch = useDispatch<any>();

  const isDev = import.meta.env.VITE_APP_ENV === "development";
  const breadcrumbs = getBreadcrumbsForRoute(location.pathname);

  useSessionData();

  const getInitials = (inputString: string) => {
    const words = inputString.split(" ");
    const initials = words.map((word) => word.charAt(0).toUpperCase());
    return initials.join("");
  };

  const handleLogout = () => {
    dispatch(logout());
    dispatch(userDataCleared());
    dispatch(organizationDataCleared());
    dispatch(membershipDataCleared());
    navigate("/login");
  };

  if (!userData) {
    return (
      <header
        className="header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 24px",
          height: "64px",
          background: "#f2f5f9",
          zIndex: 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setNavCollapsed(!isNavCollapsed)}
            className="hover:bg-white"
          >
            {isNavCollapsed ? (
              <PanelLeftOpen className="h-5 w-5" />
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </Button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
        </div>
      </header>
    );
  }

  return (
    <header
      className="header"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 24px",
        height: "64px",
        background: "#f2f5f9",
        zIndex: 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setNavCollapsed(!isNavCollapsed)}
          className="hover:bg-white"
        >
          {isNavCollapsed ? (
            <PanelLeftOpen className="h-5 w-5" />
          ) : (
            <PanelLeftClose className="h-5 w-5" />
          )}
        </Button>

        <Breadcrumb data-sentry-unmask>
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.path} style={{ display: "flex", alignItems: "center" }}>
                {index > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  {index === breadcrumbs.length - 1 ? (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink onClick={() => navigate(crumb.path)}>
                      {crumb.label}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </div>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        {isDev && (
          <Badge variant="destructive">
            <span className="md:hidden">P</span>
            <span className="hidden md:inline">Plataforma de Pruebas</span>
          </Badge>
        )}

        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-3 h-auto py-2 px-3 hover:bg-white">
              <Avatar className="h-9 w-9">
                <AvatarFallback
                  style={{
                    backgroundColor: "#0f5ef7",
                    color: "#ffffff",
                  }}
                >
                  {getInitials(`${userData.name} ${userData.first_surname}`)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start user-pii">
                <span className="text-sm font-semibold leading-tight">
                  {userData.name} {userData.first_surname}
                </span>
                <span className="text-xs text-muted-foreground leading-tight">
                  {userData.email}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 hidden md:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-sentry-unmask>
            <DropdownMenuItem onClick={() => navigate("/profile")}>
              <Icon icon="heroicons:user" className="mr-2 h-4 w-4" />
              Perfil
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/change-password")}>
              <Icon icon="heroicons:key" className="mr-2 h-4 w-4" />
              Cambiar Contraseña
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <Icon
                icon="heroicons:arrow-right-on-rectangle"
                className="mr-2 h-4 w-4"
              />
              Cerrar Sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
